import type { CollectionPagination } from '../api/use_collection/pagination';

export interface CreatePaginationOptions {
	/**
	 * Current page number (externally managed)
	 */
	currentPage: number;
	/**
	 * Total number of items across all pages
	 */
	totalCount?: number;
	/**
	 * Number of items per page
	 */
	itemsPerPage?: number;
	/**
	 * Whether there is a next page (for API-driven pagination)
	 */
	hasNext?: boolean;
	/**
	 * Whether there is a previous page (for API-driven pagination)
	 */
	hasPrev?: boolean;
	/**
	 * Callback function called when navigating to next page
	 */
	onNext: () => void;
	/**
	 * Callback function called when navigating to previous page
	 */
	onPrev: () => void;
	/**
	 * Callback function called when navigating to a specific page
	 */
	onGoToPage: (page: number) => void;
}

/**
 * Creates a pagination object with guard conditions and computed properties.
 *
 * @param options - Pagination configuration options
 * @returns A CollectionPagination object ready to use
 */
export function createPagination(
	options: CreatePaginationOptions
): CollectionPagination {
	const {
		currentPage,
		totalCount,
		itemsPerPage,
		hasNext: hasNextProp,
		hasPrev: hasPrevProp,
		onNext,
		onPrev,
		onGoToPage,
	} = options;

	const totalPages =
		totalCount !== undefined && itemsPerPage !== undefined
			? Math.ceil(totalCount / itemsPerPage)
			: undefined;

	const hasPrev = hasPrevProp !== undefined ? hasPrevProp : currentPage > 1;
	const hasNext =
		hasNextProp !== undefined
			? hasNextProp
			: totalPages !== undefined
				? currentPage < totalPages
				: false;

	return {
		currentPage,
		totalCount: totalCount ?? 0,
		count: itemsPerPage,
		hasPrev,
		hasNext,
		fetchNext: () => {
			if (hasNext) {
				onNext();
			}
		},
		fetchPrev: () => {
			if (hasPrev) {
				onPrev();
			}
		},
		fetchPage: (page: number) => {
			if (page >= 1 && (totalPages === undefined || page <= totalPages)) {
				onGoToPage(page);
			}
		},
	};
}
